import React, { useState, useMemo } from 'react';
import { Calculator, RefreshCw, TrendingUp, Users, GitBranch } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { formatCurrency } from '../../lib/utils';

export const FinancialCalculator = ({ project, transactions, jvAgreements }) => {
  // Calculate actual values from transactions
  const actualExpenses = useMemo(() => {
    return transactions
      .filter(t => t.type === 'Expense')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
  }, [transactions]);

  const actualPurchasePrice = useMemo(() => {
    const purchaseTx = transactions.find(t => t.type === 'Purchase_Price');
    return purchaseTx?.amount || project?.purchase_price || 0;
  }, [transactions, project]);

  const actualEMD = useMemo(() => {
    return transactions
      .filter(t => t.type === 'EMD')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
  }, [transactions]);

  // Flip scenario state
  const [flipScenario, setFlipScenario] = useState({
    salePrice: project?.sale_price || project?.purchase_price * 1.5 || 50000,
    purchasePrice: actualPurchasePrice,
    expenses: actualExpenses,
    jvSplit: jvAgreements[0]?.profit_split_percentage || 50,
    commissionRate: 10,
    hasJV: jvAgreements.length > 0,
    investorCapital: jvAgreements[0]?.capital_contributed || 0,
    preferredReturn: jvAgreements[0]?.preferred_return_percentage || 0
  });

  // Subdivide scenario state
  const [subdivideScenario, setSubdivideScenario] = useState({
    purchasePrice: actualPurchasePrice,
    numLots: 4,
    pricePerLot: 25000,
    developmentCosts: 15000, // Clearing, survey, platting, etc.
    marketingPerLot: 500,
    commissionRate: 10,
    hasJV: jvAgreements.length > 0,
    investorCapital: jvAgreements[0]?.capital_contributed || 0,
    preferredReturn: jvAgreements[0]?.preferred_return_percentage || 0,
    jvSplit: jvAgreements[0]?.profit_split_percentage || 50
  });

  // Calculate flip scenario results
  // Flow: Sale Price - Cost Basis = Gross Profit → Investor Distribution → Land Finder Gross Profit → Commission → Land Finder Net Profit
  const flipResults = useMemo(() => {
    const { salePrice, purchasePrice, expenses, jvSplit, commissionRate, hasJV, investorCapital, preferredReturn } = flipScenario;
    
    const costBasis = purchasePrice + expenses;
    const grossProfit = salePrice - costBasis;
    const grossProfitMargin = salePrice > 0 ? (grossProfit / salePrice) * 100 : 0;
    
    // First, calculate the JV waterfall (investor gets their share of gross profit)
    let investorReturn = 0;
    let investorPreferredReturn = 0;
    let investorProfitShare = 0;
    let landFinderGrossProfit = grossProfit; // Land finder's share before commission
    
    if (hasJV && investorCapital > 0) {
      investorReturn = investorCapital;
      investorPreferredReturn = investorCapital * (preferredReturn / 100);
      const profitToSplit = grossProfit - investorPreferredReturn;
      
      if (profitToSplit > 0) {
        investorProfitShare = profitToSplit * (jvSplit / 100);
        landFinderGrossProfit = profitToSplit * ((100 - jvSplit) / 100);
      } else {
        landFinderGrossProfit = profitToSplit;
      }
    }
    
    // Now calculate commission from Land Finder's gross profit (not the whole profit)
    let commission = 0;
    if (landFinderGrossProfit > 0 && commissionRate > 0) {
      commission = landFinderGrossProfit * (commissionRate / 100);
    }
    
    // Land finder's final profit after commission
    const landFinderProfit = landFinderGrossProfit - commission;
    
    const totalToInvestor = investorReturn + investorPreferredReturn + investorProfitShare;
    const investorROI = investorCapital > 0 ? ((totalToInvestor - investorCapital) / investorCapital) * 100 : 0;
    const landFinderROI = costBasis > 0 ? (landFinderProfit / costBasis) * 100 : 0;
    
    return {
      costBasis, grossProfit, grossProfitMargin, commission,
      investorReturn, investorPreferredReturn, investorProfitShare,
      totalToInvestor, landFinderGrossProfit, landFinderProfit, investorROI, landFinderROI,
      profitMargin: grossProfitMargin
    };
  }, [flipScenario]);

  // Calculate subdivide scenario results
  // Flow: Total Sale Price - Cost Basis = Gross Profit → Investor Distribution → Land Finder Gross Profit → Commission → Land Finder Net Profit
  const subdivideResults = useMemo(() => {
    const { purchasePrice, numLots, pricePerLot, developmentCosts, marketingPerLot, 
            commissionRate, hasJV, investorCapital, preferredReturn, jvSplit } = subdivideScenario;
    
    const totalSalePrice = numLots * pricePerLot;
    const totalMarketingCosts = numLots * marketingPerLot;
    const totalExpenses = developmentCosts + totalMarketingCosts;
    const costBasis = purchasePrice + totalExpenses;
    const grossProfit = totalSalePrice - costBasis;
    const grossProfitMargin = totalSalePrice > 0 ? (grossProfit / totalSalePrice) * 100 : 0;
    
    // First, calculate the JV waterfall (investor gets their share of gross profit)
    let investorReturn = 0;
    let investorPreferredReturn = 0;
    let investorProfitShare = 0;
    let landFinderGrossProfit = grossProfit; // Land finder's share before commission
    
    if (hasJV && investorCapital > 0) {
      investorReturn = investorCapital;
      investorPreferredReturn = investorCapital * (preferredReturn / 100);
      const profitToSplit = grossProfit - investorPreferredReturn;
      
      if (profitToSplit > 0) {
        investorProfitShare = profitToSplit * (jvSplit / 100);
        landFinderGrossProfit = profitToSplit * ((100 - jvSplit) / 100);
      } else {
        landFinderGrossProfit = profitToSplit;
      }
    }
    
    // Now calculate commission from Land Finder's gross profit (not the whole profit)
    let commission = 0;
    if (landFinderGrossProfit > 0 && commissionRate > 0) {
      commission = landFinderGrossProfit * (commissionRate / 100);
    }
    
    // Land finder's final profit after commission
    const landFinderProfit = landFinderGrossProfit - commission;
    const profitPerLot = numLots > 0 ? landFinderProfit / numLots : 0;
    
    const totalToInvestor = investorReturn + investorPreferredReturn + investorProfitShare;
    const investorROI = investorCapital > 0 ? ((totalToInvestor - investorCapital) / investorCapital) * 100 : 0;
    const landFinderROI = costBasis > 0 ? (landFinderProfit / costBasis) * 100 : 0;
    
    return {
      totalSalePrice, totalExpenses, costBasis, grossProfit, grossProfitMargin, commission,
      profitPerLot, investorReturn, investorPreferredReturn,
      investorProfitShare, totalToInvestor, landFinderGrossProfit, landFinderProfit, investorROI, landFinderROI,
      profitMargin: grossProfitMargin
    };
  }, [subdivideScenario]);

  const resetFlipScenario = () => {
    setFlipScenario({
      salePrice: project?.sale_price || project?.purchase_price * 1.5 || 50000,
      purchasePrice: actualPurchasePrice,
      expenses: actualExpenses,
      jvSplit: jvAgreements[0]?.profit_split_percentage || 50,
      commissionRate: 10,
      hasJV: jvAgreements.length > 0,
      investorCapital: jvAgreements[0]?.capital_contributed || 0,
      preferredReturn: jvAgreements[0]?.preferred_return_percentage || 0
    });
  };

  const resetSubdivideScenario = () => {
    setSubdivideScenario({
      purchasePrice: actualPurchasePrice,
      numLots: 4,
      pricePerLot: 25000,
      developmentCosts: 15000,
      marketingPerLot: 500,
      commissionRate: 10,
      hasJV: jvAgreements.length > 0,
      investorCapital: jvAgreements[0]?.capital_contributed || 0,
      preferredReturn: jvAgreements[0]?.preferred_return_percentage || 0,
      jvSplit: jvAgreements[0]?.profit_split_percentage || 50
    });
  };

  // Results Waterfall Component - Shows the correct profit flow
  const ResultsWaterfall = ({ results, scenario, isSubdivide = false }) => (
    <div className="border rounded-sm overflow-hidden">
      <div className="p-3 bg-muted/50 border-b">
        <p className="font-semibold text-sm">Profit Waterfall</p>
      </div>
      <div className="divide-y text-sm">
        {/* Step 1: Sale Price - Cost Basis = Gross Profit */}
        <div className="p-3 flex justify-between">
          <span>{isSubdivide ? 'Total Sale Price' : 'Sale Price'}</span>
          <span className="font-mono text-forest-600">{formatCurrency(isSubdivide ? results.totalSalePrice : scenario.salePrice)}</span>
        </div>
        <div className="p-3 flex justify-between">
          <span>Less: Cost Basis</span>
          <span className="font-mono text-red-600">-{formatCurrency(results.costBasis)}</span>
        </div>
        <div className="p-3 flex justify-between bg-muted/20">
          <span className="font-medium">Gross Profit</span>
          <span className="font-mono font-medium">{formatCurrency(results.grossProfit)}</span>
        </div>
        <div className="p-3 flex justify-between">
          <span className="pl-4 text-muted-foreground">Gross Profit Margin</span>
          <span className="font-mono text-muted-foreground">{results.grossProfitMargin?.toFixed(1) || results.profitMargin?.toFixed(1)}%</span>
        </div>
        
        {/* Step 2: JV Investor Distribution (if applicable) */}
        {scenario.hasJV && (
          <>
            <div className="p-3 bg-blue-50 border-t-2 border-blue-200">
              <p className="font-semibold text-sm text-blue-700 flex items-center gap-2">
                <Users className="w-4 h-4" /> Investor Distribution
              </p>
            </div>
            <div className="p-3 flex justify-between">
              <span className="pl-4">Capital Return</span>
              <span className="font-mono">{formatCurrency(results.investorReturn)}</span>
            </div>
            <div className="p-3 flex justify-between">
              <span className="pl-4">Preferred Return ({scenario.preferredReturn}%)</span>
              <span className="font-mono">{formatCurrency(results.investorPreferredReturn)}</span>
            </div>
            <div className="p-3 flex justify-between">
              <span className="pl-4">Profit Share ({scenario.jvSplit}%)</span>
              <span className="font-mono">{formatCurrency(results.investorProfitShare)}</span>
            </div>
            <div className="p-3 flex justify-between bg-blue-50">
              <span className="font-medium text-blue-700">Total to Investor</span>
              <span className="font-mono font-medium text-blue-700">{formatCurrency(results.totalToInvestor)}</span>
            </div>
            <div className="p-3 flex justify-between">
              <span className="pl-4">Investor ROI</span>
              <span className="font-mono">{results.investorROI.toFixed(1)}%</span>
            </div>
          </>
        )}
        
        {/* Step 3: Land Finder's Gross Profit */}
        <div className="p-3 flex justify-between bg-amber-50 border-t-2 border-amber-300">
          <span className="font-medium text-amber-700">Land Finder Gross Profit</span>
          <span className="font-mono font-medium text-amber-700">{formatCurrency(results.landFinderGrossProfit)}</span>
        </div>
        
        {/* Step 4: Commission (from Land Finder's Gross Profit) */}
        {scenario.commissionRate > 0 && (
          <div className="p-3 flex justify-between">
            <span className="pl-4">Less: Commission ({scenario.commissionRate}%)</span>
            <span className="font-mono text-amber-600">-{formatCurrency(results.commission)}</span>
          </div>
        )}
        
        {/* Step 5: Land Finder's Final Profit */}
        <div className="p-3 flex justify-between bg-forest-50 border-t-2 border-forest-300">
          <span className="font-semibold text-forest-700">Land Finder Profit</span>
          <span className="font-mono font-bold text-forest-700">{formatCurrency(results.landFinderProfit)}</span>
        </div>
        <div className="p-3 flex justify-between">
          <span className="pl-4">Land Finder ROI</span>
          <span className="font-mono">{results.landFinderROI.toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Model different financial scenarios to see projected outcomes. Changes here don&apos;t affect actual data.
      </p>

      <Tabs defaultValue="flip" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-sm">
          <TabsTrigger value="flip" className="rounded-sm data-[state=active]:bg-white">
            <TrendingUp className="w-4 h-4 mr-2" /> Flip Scenario
          </TabsTrigger>
          <TabsTrigger value="subdivide" className="rounded-sm data-[state=active]:bg-white">
            <GitBranch className="w-4 h-4 mr-2" /> Subdivide Scenario
          </TabsTrigger>
        </TabsList>

        {/* FLIP SCENARIO TAB */}
        <TabsContent value="flip">
          <div className="flex justify-end mb-4">
            <Button variant="outline" size="sm" onClick={resetFlipScenario} className="rounded-sm">
              <RefreshCw className="w-4 h-4 mr-2" /> Reset
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Flip Inputs */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm uppercase tracking-wide flex items-center gap-2">
                <Calculator className="w-4 h-4" /> Scenario Inputs
              </h3>
              
              <div className="space-y-2">
                <Label className="flex justify-between">
                  <span>Sale Price</span>
                  <span className="text-forest-600 font-mono">{formatCurrency(flipScenario.salePrice)}</span>
                </Label>
                <Input
                  type="number"
                  value={flipScenario.salePrice}
                  onChange={(e) => setFlipScenario({...flipScenario, salePrice: parseFloat(e.target.value) || 0})}
                  className="rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex justify-between">
                  <span>Purchase Price</span>
                  <span className="text-red-600 font-mono">{formatCurrency(flipScenario.purchasePrice)}</span>
                </Label>
                <Input
                  type="number"
                  value={flipScenario.purchasePrice}
                  onChange={(e) => setFlipScenario({...flipScenario, purchasePrice: parseFloat(e.target.value) || 0})}
                  className="rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex justify-between">
                  <span>Additional Expenses</span>
                  <span className="text-amber-600 font-mono">{formatCurrency(flipScenario.expenses)}</span>
                </Label>
                <Input
                  type="number"
                  value={flipScenario.expenses}
                  onChange={(e) => setFlipScenario({...flipScenario, expenses: parseFloat(e.target.value) || 0})}
                  className="rounded-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Actual: {formatCurrency(actualExpenses)} | EMD: {formatCurrency(actualEMD)}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="flex justify-between">
                  <span>Commission Rate</span>
                  <span className="font-mono">{flipScenario.commissionRate}%</span>
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={flipScenario.commissionRate}
                    onChange={(e) => setFlipScenario({...flipScenario, commissionRate: Math.min(25, Math.max(0, parseFloat(e.target.value) || 0))})}
                    className="rounded-sm w-20"
                    min={0}
                    max={25}
                    step={0.5}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                  <div className="flex-1 flex gap-1">
                    {[5, 7.5, 10, 12.5, 15].map(val => (
                      <Button
                        key={val}
                        type="button"
                        variant={flipScenario.commissionRate === val ? "default" : "outline"}
                        size="sm"
                        className={`rounded-sm text-xs px-2 ${flipScenario.commissionRate === val ? 'bg-forest-500' : ''}`}
                        onClick={() => setFlipScenario({...flipScenario, commissionRate: val})}
                      >
                        {val}%
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* JV Settings for Flip Scenario */}
              <div className="p-4 border rounded-sm bg-muted/20 space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="hasJV-flip"
                    checked={flipScenario.hasJV}
                    onChange={(e) => setFlipScenario({...flipScenario, hasJV: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="hasJV-flip" className="cursor-pointer">
                    Include JV Agreement
                  </Label>
                </div>
                
                {flipScenario.hasJV && (
                  <>
                    <div className="space-y-2">
                      <Label className="flex justify-between">
                        <span>Investor Capital</span>
                        <span className="font-mono">{formatCurrency(flipScenario.investorCapital)}</span>
                      </Label>
                      <Input
                        type="number"
                        value={flipScenario.investorCapital}
                        onChange={(e) => setFlipScenario({...flipScenario, investorCapital: parseFloat(e.target.value) || 0})}
                        className="rounded-sm"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex justify-between">
                        <span>Preferred Return</span>
                        <span className="font-mono">{flipScenario.preferredReturn}%</span>
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={flipScenario.preferredReturn}
                          onChange={(e) => setFlipScenario({...flipScenario, preferredReturn: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0))})}
                          className="rounded-sm w-20"
                          min={0}
                          max={100}
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                        <div className="flex-1 flex gap-1">
                          {[0, 5, 8, 10, 12, 15].map(val => (
                            <Button
                              key={val}
                              type="button"
                              variant={flipScenario.preferredReturn === val ? "default" : "outline"}
                              size="sm"
                              className={`rounded-sm text-xs px-2 ${flipScenario.preferredReturn === val ? 'bg-forest-500' : ''}`}
                              onClick={() => setFlipScenario({...flipScenario, preferredReturn: val})}
                            >
                              {val}%
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex justify-between">
                        <span>Investor Profit Split</span>
                        <span className="font-mono">{flipScenario.jvSplit}%</span>
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={flipScenario.jvSplit}
                          onChange={(e) => setFlipScenario({...flipScenario, jvSplit: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0))})}
                          className="rounded-sm w-20"
                          min={0}
                          max={100}
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                        <div className="flex-1 flex gap-1">
                          {[25, 40, 50, 60, 70, 75].map(val => (
                            <Button
                              key={val}
                              type="button"
                              variant={flipScenario.jvSplit === val ? "default" : "outline"}
                              size="sm"
                              className={`rounded-sm text-xs px-2 ${flipScenario.jvSplit === val ? 'bg-forest-500' : ''}`}
                              onClick={() => setFlipScenario({...flipScenario, jvSplit: val})}
                            >
                              {val}%
                            </Button>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        Investor: {flipScenario.jvSplit}% | Land Finder: {100 - flipScenario.jvSplit}%
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Flip Results */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm uppercase tracking-wide flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Projected Results
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/30 rounded-sm border">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Cost Basis</p>
                  <p className="text-lg font-heading font-bold font-mono text-red-600">
                    {formatCurrency(flipResults.costBasis)}
                  </p>
                </div>
                <div className="p-3 bg-muted/30 rounded-sm border">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Gross Profit</p>
                  <p className={`text-lg font-heading font-bold font-mono ${flipResults.grossProfit >= 0 ? 'text-forest-600' : 'text-red-600'}`}>
                    {formatCurrency(flipResults.grossProfit)}
                  </p>
                </div>
                <div className="p-3 bg-muted/30 rounded-sm border">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Profit Margin</p>
                  <p className="text-lg font-heading font-bold font-mono">
                    {flipResults.profitMargin.toFixed(1)}%
                  </p>
                </div>
                <div className="p-3 bg-muted/30 rounded-sm border">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Commission</p>
                  <p className="text-lg font-heading font-bold font-mono text-amber-600">
                    {formatCurrency(flipResults.commission)}
                  </p>
                </div>
              </div>

              <ResultsWaterfall results={flipResults} scenario={flipScenario} />
            </div>
          </div>
        </TabsContent>

        {/* SUBDIVIDE SCENARIO TAB */}
        <TabsContent value="subdivide">
          <div className="flex justify-end mb-4">
            <Button variant="outline" size="sm" onClick={resetSubdivideScenario} className="rounded-sm">
              <RefreshCw className="w-4 h-4 mr-2" /> Reset
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Subdivide Inputs */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm uppercase tracking-wide flex items-center gap-2">
                <GitBranch className="w-4 h-4" /> Subdivision Inputs
              </h3>
              
              <div className="space-y-2">
                <Label className="flex justify-between">
                  <span>Land Purchase Price</span>
                  <span className="text-red-600 font-mono">{formatCurrency(subdivideScenario.purchasePrice)}</span>
                </Label>
                <Input
                  type="number"
                  value={subdivideScenario.purchasePrice}
                  onChange={(e) => setSubdivideScenario({...subdivideScenario, purchasePrice: parseFloat(e.target.value) || 0})}
                  className="rounded-sm"
                />
              </div>

              <div className="p-4 border rounded-sm bg-blue-50/50 space-y-4">
                <p className="text-sm font-medium text-blue-800">Lot Configuration</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex justify-between">
                      <span>Number of Lots</span>
                      <span className="font-mono">{subdivideScenario.numLots}</span>
                    </Label>
                    <Input
                      type="number"
                      value={subdivideScenario.numLots}
                      onChange={(e) => setSubdivideScenario({...subdivideScenario, numLots: parseInt(e.target.value) || 1})}
                      className="rounded-sm"
                      min={1}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex justify-between">
                      <span>Price per Lot</span>
                      <span className="text-forest-600 font-mono">{formatCurrency(subdivideScenario.pricePerLot)}</span>
                    </Label>
                    <Input
                      type="number"
                      value={subdivideScenario.pricePerLot}
                      onChange={(e) => setSubdivideScenario({...subdivideScenario, pricePerLot: parseFloat(e.target.value) || 0})}
                      className="rounded-sm"
                    />
                  </div>
                </div>
                
                <div className="p-2 bg-white rounded border text-center">
                  <p className="text-xs text-muted-foreground">Total Sale Price</p>
                  <p className="text-lg font-bold text-forest-600 font-mono">
                    {formatCurrency(subdivideScenario.numLots * subdivideScenario.pricePerLot)}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex justify-between">
                  <span>Development Costs</span>
                  <span className="text-amber-600 font-mono">{formatCurrency(subdivideScenario.developmentCosts)}</span>
                </Label>
                <Input
                  type="number"
                  value={subdivideScenario.developmentCosts}
                  onChange={(e) => setSubdivideScenario({...subdivideScenario, developmentCosts: parseFloat(e.target.value) || 0})}
                  className="rounded-sm"
                />
                <p className="text-xs text-muted-foreground">Survey, platting, clearing, legal, etc.</p>
              </div>

              <div className="space-y-2">
                <Label className="flex justify-between">
                  <span>Marketing Cost per Lot</span>
                  <span className="text-amber-600 font-mono">{formatCurrency(subdivideScenario.marketingPerLot)}</span>
                </Label>
                <Input
                  type="number"
                  value={subdivideScenario.marketingPerLot}
                  onChange={(e) => setSubdivideScenario({...subdivideScenario, marketingPerLot: parseFloat(e.target.value) || 0})}
                  className="rounded-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Total: {formatCurrency(subdivideScenario.marketingPerLot * subdivideScenario.numLots)} for {subdivideScenario.numLots} lots
                </p>
              </div>

              <div className="space-y-2">
                <Label className="flex justify-between">
                  <span>Commission Rate</span>
                  <span className="font-mono">{subdivideScenario.commissionRate}%</span>
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={subdivideScenario.commissionRate}
                    onChange={(e) => setSubdivideScenario({...subdivideScenario, commissionRate: Math.min(25, Math.max(0, parseFloat(e.target.value) || 0))})}
                    className="rounded-sm w-20"
                    min={0}
                    max={25}
                    step={0.5}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                  <div className="flex-1 flex gap-1">
                    {[5, 7.5, 10, 12.5, 15].map(val => (
                      <Button
                        key={val}
                        type="button"
                        variant={subdivideScenario.commissionRate === val ? "default" : "outline"}
                        size="sm"
                        className={`rounded-sm text-xs px-2 ${subdivideScenario.commissionRate === val ? 'bg-forest-500' : ''}`}
                        onClick={() => setSubdivideScenario({...subdivideScenario, commissionRate: val})}
                      >
                        {val}%
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* JV Settings for Subdivide Scenario */}
              <div className="p-4 border rounded-sm bg-muted/20 space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="hasJV-sub"
                    checked={subdivideScenario.hasJV}
                    onChange={(e) => setSubdivideScenario({...subdivideScenario, hasJV: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="hasJV-sub" className="cursor-pointer">
                    Include JV Agreement
                  </Label>
                </div>
                
                {subdivideScenario.hasJV && (
                  <>
                    <div className="space-y-2">
                      <Label className="flex justify-between">
                        <span>Investor Capital</span>
                        <span className="font-mono">{formatCurrency(subdivideScenario.investorCapital)}</span>
                      </Label>
                      <Input
                        type="number"
                        value={subdivideScenario.investorCapital}
                        onChange={(e) => setSubdivideScenario({...subdivideScenario, investorCapital: parseFloat(e.target.value) || 0})}
                        className="rounded-sm"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex justify-between">
                        <span>Preferred Return</span>
                        <span className="font-mono">{subdivideScenario.preferredReturn}%</span>
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={subdivideScenario.preferredReturn}
                          onChange={(e) => setSubdivideScenario({...subdivideScenario, preferredReturn: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0))})}
                          className="rounded-sm w-20"
                          min={0}
                          max={100}
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                        <div className="flex-1 flex gap-1">
                          {[0, 5, 8, 10, 12, 15].map(val => (
                            <Button
                              key={val}
                              type="button"
                              variant={subdivideScenario.preferredReturn === val ? "default" : "outline"}
                              size="sm"
                              className={`rounded-sm text-xs px-2 ${subdivideScenario.preferredReturn === val ? 'bg-forest-500' : ''}`}
                              onClick={() => setSubdivideScenario({...subdivideScenario, preferredReturn: val})}
                            >
                              {val}%
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex justify-between">
                        <span>Investor Profit Split</span>
                        <span className="font-mono">{subdivideScenario.jvSplit}%</span>
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={subdivideScenario.jvSplit}
                          onChange={(e) => setSubdivideScenario({...subdivideScenario, jvSplit: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0))})}
                          className="rounded-sm w-20"
                          min={0}
                          max={100}
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                        <div className="flex-1 flex gap-1">
                          {[25, 40, 50, 60, 70, 75].map(val => (
                            <Button
                              key={val}
                              type="button"
                              variant={subdivideScenario.jvSplit === val ? "default" : "outline"}
                              size="sm"
                              className={`rounded-sm text-xs px-2 ${subdivideScenario.jvSplit === val ? 'bg-forest-500' : ''}`}
                              onClick={() => setSubdivideScenario({...subdivideScenario, jvSplit: val})}
                            >
                              {val}%
                            </Button>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        Investor: {subdivideScenario.jvSplit}% | Land Finder: {100 - subdivideScenario.jvSplit}%
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Subdivide Results */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm uppercase tracking-wide flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Projected Results
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/30 rounded-sm border">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Cost Basis</p>
                  <p className="text-lg font-heading font-bold font-mono text-red-600">
                    {formatCurrency(subdivideResults.costBasis)}
                  </p>
                </div>
                <div className="p-3 bg-muted/30 rounded-sm border">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Gross Profit</p>
                  <p className={`text-lg font-heading font-bold font-mono ${subdivideResults.grossProfit >= 0 ? 'text-forest-600' : 'text-red-600'}`}>
                    {formatCurrency(subdivideResults.grossProfit)}
                  </p>
                </div>
                <div className="p-3 bg-muted/30 rounded-sm border">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Profit per Lot</p>
                  <p className="text-lg font-heading font-bold font-mono text-forest-600">
                    {formatCurrency(subdivideResults.profitPerLot)}
                  </p>
                </div>
                <div className="p-3 bg-muted/30 rounded-sm border">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Commission</p>
                  <p className="text-lg font-heading font-bold font-mono text-amber-600">
                    {formatCurrency(subdivideResults.commission)}
                  </p>
                </div>
              </div>

              {/* Cost Breakdown */}
              <div className="p-4 border rounded-sm bg-muted/10">
                <p className="text-sm font-medium mb-3">Cost Breakdown</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Land Purchase</span>
                    <span className="font-mono">{formatCurrency(subdivideScenario.purchasePrice)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Development Costs</span>
                    <span className="font-mono">{formatCurrency(subdivideScenario.developmentCosts)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Marketing ({subdivideScenario.numLots} lots × {formatCurrency(subdivideScenario.marketingPerLot)})</span>
                    <span className="font-mono">{formatCurrency(subdivideScenario.marketingPerLot * subdivideScenario.numLots)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t font-medium">
                    <span>Total Cost Basis</span>
                    <span className="font-mono">{formatCurrency(subdivideResults.costBasis)}</span>
                  </div>
                </div>
              </div>

              <ResultsWaterfall results={subdivideResults} scenario={subdivideScenario} isSubdivide={true} />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
